import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";

export default function Login() {
  const [mode, setMode] = useState("login"); // or 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { loginWithEmail, registerEmail } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await loginWithEmail(email, password);
      else await registerEmail(email, password, name || email.split("@")[0]);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auth failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 grid grid-cols-1 md:grid-cols-2" style={{ fontFamily: "Manrope" }}>
      {/* Left visual panel */}
      <div className="hidden md:flex relative overflow-hidden border-r border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(59,130,246,0.12),transparent_60%),radial-gradient(ellipse_at_bottom_right,rgba(14,165,233,0.08),transparent_60%)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#30363d 1px, transparent 1px), linear-gradient(90deg, #30363d 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative z-10 p-10 flex flex-col justify-between w-full">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <span className="text-[11px] font-mono font-bold text-black">S1</span>
            </div>
            <div className="text-sm tracking-tight font-semibold">RSOC · Facebook Hub</div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-slate-500 font-mono">
              <BarChart3 className="w-3 h-3" /> Unified Ops · 2026
            </div>
            <h1 className="text-4xl font-semibold tracking-tight leading-tight mb-4">
              One screen for<br />
              <span className="text-slate-500">buyside × sellside arbitrage.</span>
            </h1>
            <p className="text-sm text-slate-500 max-w-md leading-relaxed">
              Real-time Facebook spend meets System1 RSOC revenue. AI builds the hooks,
              writes the copy, paints the ugly ads, and tells you which verticals to scale.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "Sources", v: "FB + S1" },
                { k: "AI Models", v: "Claude 4.5" },
                { k: "Images", v: "Nano Banana" },
              ].map((m) => (
                <div key={m.k} className="border border-slate-200 rounded-md p-3 bg-white/40">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{m.k}</div>
                  <div className="text-sm font-medium mt-1 font-mono">{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-500 font-mono">
            © 2026 · RSOC-FB Hub · Internal preview
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-semibold tracking-tight mb-1" data-testid="login-title">
            {mode === "login" ? "Sign in" : "Create account"}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {mode === "login" ? "Welcome back. Let's look at your P&L." : "Start tracking profit across FB + System1."}
          </p>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <input
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60"
              />
            )}
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@work.com"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60"
            />
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/60"
            />
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full bg-blue-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-4 text-xs text-slate-500 flex items-center justify-between">
            <div>
              {mode === "login" ? "No account? " : "Already have one? "}
              <button
                data-testid="toggle-auth-mode"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-slate-900 hover:underline"
              >
                {mode === "login" ? "Create one" : "Sign in"}
              </button>
            </div>
            {mode === "login" && (
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-slate-900 hover:underline">
                Forgot password?
              </Link>
            )}
          </div>

          <div className="mt-8 pt-4 border-t border-slate-100 text-[11px] text-slate-400 font-mono flex items-center gap-3" data-testid="legal-footer">
            <Link to="/terms" className="hover:text-slate-700">Terms</Link>
            <span>·</span>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
